// Three dimensional vectors and rotations, as plain tuples. The die uses them.
// The axes are x to the right, y up, and z toward the viewer.

export type Vec3 = readonly [number, number, number];
/** A rotation, as three rows. */
export type Mat3 = readonly [Vec3, Vec3, Vec3];

type Axis = 0 | 1 | 2;

export const dot = (a: Vec3, b: Vec3): number => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];

export const cross = (a: Vec3, b: Vec3): Vec3 => [
  a[1] * b[2] - a[2] * b[1],
  a[2] * b[0] - a[0] * b[2],
  a[0] * b[1] - a[1] * b[0],
];

export const subtract = (a: Vec3, b: Vec3): Vec3 => [a[0] - b[0], a[1] - b[1], a[2] - b[2]];

export const scale = (v: Vec3, factor: number): Vec3 => [
  v[0] * factor,
  v[1] * factor,
  v[2] * factor,
];

/** A vector with no length stays as it is. */
export const normalize = (v: Vec3): Vec3 => {
  const length = Math.hypot(v[0], v[1], v[2]);
  return length === 0 ? v : scale(v, 1 / length);
};

export const rotate = (m: Mat3, v: Vec3): Vec3 => [dot(m[0], v), dot(m[1], v), dot(m[2], v)];

const column = (m: Mat3, axis: Axis): Vec3 => [m[0][axis], m[1][axis], m[2][axis]];

/** The rotation that does `b` first, then `a`. */
export const multiply = (a: Mat3, b: Mat3): Mat3 => {
  const columns: Mat3 = [column(b, 0), column(b, 1), column(b, 2)];
  const row = (r: Vec3): Vec3 => [dot(r, columns[0]), dot(r, columns[1]), dot(r, columns[2])];
  return [row(a[0]), row(a[1]), row(a[2])];
};

/** A turn of `angle` radians about a unit `axis`, by the right hand rule. */
export const axisAngle = (axis: Vec3, angle: number): Mat3 => {
  const [x, y, z] = axis;
  const c = Math.cos(angle);
  const s = Math.sin(angle);
  const t = 1 - c;
  return [
    [c + t * x * x, t * x * y - s * z, t * x * z + s * y],
    [t * x * y + s * z, c + t * y * y, t * y * z - s * x],
    [t * x * z - s * y, t * y * z + s * x, c + t * z * z],
  ];
};
